import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { djs } from '@duke-hq/djs';
import {
  ProviderCauseVisibilitySchema,
  type ProviderCauseVisibility,
} from '@duke-hq/svc-api-db/schemas/enums/ProviderCauseVisibility.schema';
import {
  CauseFormInput,
  CauseFormSchema,
  CreateCauseSchema,
} from '@duke-hq/svc-api/provider/trpc/routers/tenant/groupRewards/schemas';
import { FileUploadContextDirect } from '@duke-hq/svc-files-ui/FileUploadContextDirect';
import { FileUploadTrigger } from '@duke-hq/svc-files-ui/FileUploadTrigger';
import { ImageUploadDisplay } from '@duke-hq/svc-files-ui/ImageUploadDisplay';
import { ACCEPTABLE_IMAGES } from '@duke-hq/svc-files-lib/schemas';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Select, SingleValue } from 'chakra-react-select';
import { ImagePlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { CauseFormVisibilityFields } from './CauseFormVisibilityFields';
import { ProviderCauseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/ProviderCauseStatus.schema';

type SelectOption = {
  label: string;
  value: string;
};

type CauseCardProps = {
  name: string;
  imageUrl: string | null;
  targetAmount: number;
  visibility: ProviderCauseVisibility;
};

const defaultFormState: CauseFormInput = {
  name: '',
  description: undefined,
  websiteUrl: 'https://',
  targetAmount: 5,
  timeframeDate: '',
  visibility: undefined,
  siteRegionIds: undefined,
  siteLocationIds: undefined,
  states: undefined,
  rationale: undefined,
  imageDrn: undefined,
};

function CauseCard({
  cause,
  locations,
}: {
  cause: CauseCardProps;
  locations: SelectOption[] | null;
}) {
  const { name, imageUrl, targetAmount, visibility } = cause;

  const locationLabel = useMemo(() => {
    const labelMap = {
      [ProviderCauseVisibilitySchema.enum.REGION]: {
        prefix: 'Regional',
        plural: 'regions',
      },
      [ProviderCauseVisibilitySchema.enum.SITE]: {
        prefix: 'Local',
        plural: 'sites',
      },
      [ProviderCauseVisibilitySchema.enum.STATE]: {
        prefix: 'State',
        plural: 'states',
      },
    };

    if (visibility === ProviderCauseVisibilitySchema.enum.NATIONAL) {
      return (
        <Text fontSize="sm" color="fg.muted">
          National
        </Text>
      );
    }

    const label = labelMap[visibility] ?? {
      prefix: 'Location',
      plural: 'locations',
    };

    if (locations != null && locations.length > 1) {
      return (
        <Flex fontSize="sm" color="fg.muted" align="center" gap={1}>
          <Text fontWeight="semibold">{label.prefix}:</Text>

          {visibility === ProviderCauseVisibilitySchema.enum.STATE ? (
            locations.map((loc) => loc.value).join(',')
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="5px"
              borderColor="brand.500"
              bg="brand.500"
              color="white"
              borderWidth="1px"
              px={2}
              fontSize="xs"
              fontWeight="bold"
              sx={{ textTransform: 'none' }}
            >
              {locations.length} {label.plural}
            </Box>
          )}
        </Flex>
      );
    }

    return (
      <Text fontSize="sm" color="fg.muted">
        <Text as="span" fontWeight="semibold">
          {label.prefix}:
        </Text>{' '}
        {locations?.[0]?.label ?? 'N/A'}
      </Text>
    );
  }, [visibility, locations]);

  return (
    <Box
      bg="bg.subtle"
      borderRadius="lg"
      boxShadow="sm"
      p={2}
      maxW="md"
      w="full"
      display="flex"
      alignItems="center"
      gap={4}
    >
      {imageUrl == null ? (
        <Flex
          w="200px"
          h="100px"
          borderRadius="md"
          bg="bg.muted"
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={ImagePlus} boxSize={6} color="fg.muted" />
        </Flex>
      ) : (
        <Stack w="200px" h="100px">
          <Image
            src={typeof imageUrl === 'string' ? imageUrl : undefined}
            boxSize="100px"
            objectFit="cover"
            borderRadius="md"
            w="full"
          />
        </Stack>
      )}
      <Flex direction="column" alignItems="flex-start" textAlign="left">
        <Text fontWeight="bold" fontSize="sm" mb={1}>
          {name}
        </Text>

        <Text fontSize="sm" color="fg.muted">
          <Text as="span" fontWeight="semibold">
            Goal:
          </Text>{' '}
          ${targetAmount}
        </Text>

        {locationLabel}
      </Flex>
    </Box>
  );
}

export function AdminCreateCauseForm({ onClose }: { onClose: () => void }): React.ReactElement {
  const trpcUtils = trpcTenant.useUtils();
  const [nameToValidate, setNameToValidate] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [selectedVisibilityLocations, setSelectedVisibilityLocations] = useState<
    SelectOption[] | null
  >(null);
  const [newCause, setNewCause] = useState<CauseCardProps | null>(null);

  const toast = useToast();
  const utils = trpcTenant.useUtils();

  const visibilityOptions = useMemo<SelectOption[]>(
    () => [
      { label: t`National`, value: ProviderCauseVisibilitySchema.enum.NATIONAL },
      { label: t`Region`, value: ProviderCauseVisibilitySchema.enum.REGION },
      { label: t`Site`, value: ProviderCauseVisibilitySchema.enum.SITE },
      { label: t`State`, value: ProviderCauseVisibilitySchema.enum.STATE },
    ],
    [],
  );

  const methods = useForm<CauseFormInput>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(CauseFormSchema),
    defaultValues: defaultFormState,
  });

  const {
    handleSubmit,
    register,
    clearErrors,
    setValue,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const name = useWatch({
    control,
    name: 'name',
  });

  const visibility = useWatch({
    control,
    name: 'visibility',
  });

  const createCause = trpcTenant.groupRewards.createCause.useMutation({
    onSuccess: (data) => {
      setNewCause({
        name: data.name,
        imageUrl: data.imageUrl ?? null,
        targetAmount: data.targetAmount,
        visibility: data.visibility ?? ProviderCauseVisibilitySchema.enum.NATIONAL,
      });

      void trpcUtils.groupRewards.listCausesWithInfiniteScroll.invalidate();
      setIsSuccess(true);

      toast({
        title: t`Success`,
        description: t`Cause created successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: error.message || t`Failed to create a provider cause.`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    onSettled() {
      void utils.groupRewards.listCauses.refetch();
    },
  });

  const { refetch: checkNameExistence } = trpcTenant.groupRewards.validateUniqueName.useQuery(
    { name },
    { enabled: Boolean(nameToValidate) },
  );

  const validateName = async () => {
    if (name == null) {
      return true;
    }

    setNameToValidate(true);
    const result = await checkNameExistence();
    setNameToValidate(false);

    return !(result.data ?? false);
  };

  const onSubmit = async (data: CauseFormInput) => {
    const isNameValid = await validateName();

    if (isNameValid === false) {
      setError('name', {
        type: 'manual',
        message: t`A cause with this name already exists. Please choose a different name.`,
      });

      setNameToValidate(false);
      return;
    }

    // Transform form data to API payload using the schema
    const payload = CreateCauseSchema.parse({
      ...data,
      // The schema will handle coercing timeframeDate to Date via z.coerce.date()
      // And handle all the nullishToUndefined transformations
    });

    await createCause.mutateAsync({
      ...payload,
      status: ProviderCauseStatusSchema.enum.APPROVED,
    });
  };

  return (
    <FormProvider {...methods}>
      <Box as="form" onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }} noValidate>
        <VStack spacing={4} align="stretch" position="relative">
          {isSuccess === true && newCause != null ? (
            <VStack
              position="absolute"
              inset="0"
              zIndex="10"
              bg="rgba(255, 255, 255, 0.9)"
              backdropFilter="blur(5px)"
              align="center"
              justify="center"
              borderRadius="md"
              textAlign="center"
              spacing={4}
            >
              <CauseCard cause={newCause} locations={selectedVisibilityLocations} />
              <Text fontSize="2xl" fontWeight="bold" color="brand.500" mt={4}>
                <Trans>Cause created!</Trans>
              </Text>
              <Button onClick={onClose} mt={6} variant="outline">
                <Trans>Done</Trans>
              </Button>
            </VStack>
          ) : null}

          <Stack justify="flex-start" align="start" px={6}>
            <Flex justify="space-between" align="center" w="full">
              <Text size="md" fontWeight="bold">
                <Trans>Create a cause</Trans>
              </Text>
            </Flex>
          </Stack>

          <FormMainContainer>
            <FormContentContainer>
              <FormControl>
                <Controller
                  control={control}
                  name="imageDrn"
                  render={({ field }) => (
                    <FileUploadContextDirect
                      quantity={{ min: 1, max: 1 }}
                      accept={ACCEPTABLE_IMAGES}
                      attachedToKind="cause"
                      onFileComplete={(result) => field.onChange(result.drn)}
                    >
                      <Stack h="132px" alignItems="center">
                        <FileUploadTrigger>
                          <Stack
                            bg="bg.subtle"
                            p={4}
                            rounded="10px"
                            w="178px"
                            h="132px"
                            cursor="pointer"
                            userSelect="none"
                            position="relative"
                          >
                            <ImageUploadDisplay />
                          </Stack>
                        </FileUploadTrigger>
                      </Stack>
                    </FileUploadContextDirect>
                  )}
                />
              </FormControl>
              <FormControl isRequired isInvalid={!!errors.name}>
                <FormLabel htmlFor="CauseForm-name">
                  <Trans>Name of cause</Trans>
                </FormLabel>
                <Input
                  id="CauseForm-name"
                  autoComplete="off"
                  bg={'white'}
                  color={'inherit'}
                  {...register('name')}
                />
                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.websiteUrl}>
                <FormLabel htmlFor="CauseForm-website">
                  <Trans>Website URL</Trans>
                </FormLabel>
                <Input
                  id="CauseForm-website"
                  autoComplete="off"
                  bg={'white'}
                  color={'inherit'}
                  {...register('websiteUrl')}
                />
                <FormErrorMessage>{errors.websiteUrl?.message}</FormErrorMessage>
              </FormControl>

              <HStack w="full" align="center" gap={2}>
                <FormControl isRequired isInvalid={!!errors.targetAmount}>
                  <FormLabel htmlFor="CauseForm-targetAmount">
                    <Trans>Target amount</Trans>
                  </FormLabel>
                  <Controller
                    name="targetAmount"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        min={5}
                        precision={2}
                        step={5}
                        onChange={(_, valueAsNumber) => {
                          field.onChange(valueAsNumber);
                        }}
                        value={field.value ?? ''}
                      >
                        <NumberInputField
                          id="CauseForm-targetAmount"
                          bg={'white'}
                          color={'inherit'}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    )}
                  />
                  <FormErrorMessage>{errors.targetAmount?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.timeframeDate}>
                  <FormLabel htmlFor="CauseForm-timeframeDate">
                    <Trans>End date</Trans>
                  </FormLabel>
                  <Input
                    id="CauseForm-timeframeDate"
                    type="date"
                    min={djs().format('YYYY-MM-DD')}
                    bg={'white'}
                    color={'inherit'}
                    {...register('timeframeDate')}
                  />
                  <FormErrorMessage>{errors.timeframeDate?.message}</FormErrorMessage>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel htmlFor="CauseForm-description">
                  <Trans>Description</Trans>
                </FormLabel>
                <Textarea
                  id="CauseForm-description"
                  placeholder="Add a description"
                  size="sm"
                  bg={'white'}
                  color={'inherit'}
                  {...register('description')}
                />
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.visibility}>
                <FormLabel htmlFor="CauseForm-visibility">
                  <Trans>Visibility</Trans>
                </FormLabel>
                <Controller
                  name="visibility"
                  control={control}
                  render={({ field }) => (
                    <Select
                      id="visibility"
                      placeholder={t`Site, Region, State or National`}
                      options={visibilityOptions}
                      styles={{
                        control: (provided: React.CSSProperties) => ({
                          ...provided,
                          backgroundColor: 'white',
                          color: 'inherit',
                        }),
                      }}
                      value={
                        visibilityOptions?.find((option) => option.value === field.value) || null
                      }
                      onChange={(selectedVisibility: SingleValue<SelectOption> | null) => {
                        field.onChange(selectedVisibility?.value ?? undefined);

                        // Reset dependent fields when visibility changes
                        setValue('siteRegionIds', undefined);
                        setValue('siteLocationIds', undefined);
                        setValue('states', undefined);

                        clearErrors(['siteRegionIds', 'siteLocationIds', 'states']);
                      }}
                    />
                  )}
                />
                <FormErrorMessage>{errors.visibility?.message}</FormErrorMessage>
              </FormControl>

              {visibility != null && (
                <CauseFormVisibilityFields
                  visibility={visibility}
                  onSelect={(selected: SelectOption[] | null) => {
                    if (selected == null) {
                      return;
                    }

                    setSelectedVisibilityLocations(selected);
                  }}
                />
              )}

              <FormControl>
                <FormLabel htmlFor="CauseForm-rationale">
                  <Trans>What is the main reason for creating this cause?</Trans>
                </FormLabel>
                <Textarea
                  id="CauseForm-rationale"
                  placeholder={t`Add any further details or rationale`}
                  size="sm"
                  bg={'white'}
                  color={'inherit'}
                  {...register('rationale')}
                />
              </FormControl>
            </FormContentContainer>
          </FormMainContainer>
          <FormFooterContainer>
            <HStack justify="flex-end" align="center" gap={2}>
              <Button variant="ghost" onClick={onClose}>
                <Trans>Close</Trans>
              </Button>

              <Button type="submit" isLoading={isSubmitting || createCause.isPending}>
                <Trans>Create</Trans>
              </Button>
            </HStack>
          </FormFooterContainer>
        </VStack>
      </Box>
    </FormProvider>
  );
}
